<template>
  <div>
    <vx-card class="mt-base" :title="activityTitle">
      <p class="mb-5">
        In this activity you are going to either explore or participate in “Citizen Science” that's
        really improving Australia's capacity to understand how we can adapt and how we are being
        affected by climate change.
      </p>
      <p class="mb-5">
        Citizen science is the collection and analysis of scientific data in relation to the natural
        world, performed predominantly by citizens, usually in collaboration with scientists and
        field experts.
      </p>
      <p class="mb-5">
        The Australian Citizen Science Association (ACSA) has a
        <a href="https://citizenscience.org.au/ala-project-finder/" target="_blank"
          >Project Finder</a
        >, developed in partnership with the Atlas of Living Australia (ALA), right across Australia
        where you can discover and connect with citizen science projects that include
        Australian-based biodiversity related projects of all different scales and themes. You can
        <strong
          >search for projects in your local area or search for projects related to a particular
          subject or theme.</strong
        >
      </p>
    </vx-card>
    <vx-card title="Who are these people/organisations?">
      <p class="mb-5">
        The following link and video will give you some information on who these are:
      </p>

      <vs-row vs-align="center" vs-type="flex" vs-justify="center" vs-w="12">
        <vs-col vs-type="flex" vs-justify="center" vs-lg="6" vs-sm="6" vs-xs="12">
          <button-link-wrapper
            text="“Who is the ASCA?”"
            link="https://citizenscience.org.au/who-is-acsa"
          />
        </vs-col>
        <vs-col vs-lg="6" vs-sm="6" vs-xs="12">
          <button
            type="button"
            class="no-style img-click-button"
            aria-label="Play video: Who is the Atlas of Living Australia?"
            @click="showVideoPopup(videos[1].video)"
          >
            <img :src="videos[1].image" class="img-responsive mx-auto cursor-pointer" alt="" />
          </button>
        </vs-col>
      </vs-row>
    </vx-card>

    <vx-card>
      <p class="mb-5">
        In this activity you have 3 different options and you must complete
        <strong><u>AT LEAST</u></strong> one of them:
      </p>

      <p class="mb-5"><strong>Option 1:</strong> Explore Projects</p>
      <p class="mb-5">
        <strong>Option 2:</strong> Explore Projects + View and Record Details of a Selected Project
      </p>
      <p class="mb-5">
        <strong>Option 3:</strong> Explore Projects + View and Record Details of a Selected Project
        + Participate in a Project.
      </p>
      <p class="mb-5">
        No matter which option(s) you choose, you will need to go to the Citizen Science “Project
        Finder”. Here you will see 100’s of project tiles (<button
          type="button"
          class="no-style inline-link"
          @click="showIframePopup(popupImages[1])"
        >
          click here to preview</button
        >) as well as a filter (<button
          type="button"
          class="no-style inline-link"
          @click="showIframePopup(popupImages[2])"
        >
          click here to preview</button
        >) on the left-hand side so you can narrow the options down to suit your needs and
        preferences.
      </p>

      <a
        href="https://biocollect.ala.org.au/acsa?hub=ala-cs#isCitizenScience%3Dtrue%26isWorldWide%3Dfalse%26max%3D20%26sort%3DdateCreatedSort"
        target="_blank"
      >
        <centered-image-wrapper
          classes="cursor-pointer"
          :image="image1"
          alt="Browse citizen science projects on BioCollect (opens in a new tab)"
        />
      </a>

      <p class="mb-5">
        You can access each of the Tasks/Options via the tabs below. When you have completed as many
        of the tasks as you are going to do, you then need to scroll to the bottom of the page and
        click the “Save Activity” button to finalise this whole activity.
      </p>
    </vx-card>

    <vs-tabs alignment="fixed" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Task 1/Option 1: Explore Projects">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">Task 1/Option 1: Explore Projects</h4>
            <p class="mb-5">
              This is simply a matter of going to the Australian Citizen Science Project Finder (via
              the link above) and using the filter to explore projects. Then, in the boxes below,
              record the project that most appeals to you and why.
            </p>

            <p class="mb-1">The project that most appeals to me is:</p>
            <text-block v-model="answers.question1" :editable="isEditable" />

            <p class="mb-1">The reason for this is:</p>
            <textarea-block v-model="answers.question2" :editable="isEditable" />
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Task 2/Option 2: View and Record Details of a Selected Project">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">Task 2/Option 2: View and Record Details of a Selected Project</h4>
            <p class="mb-5">
              This option allows you to delve a little deeper into the project that most appeals to
              you. When you click on your preferred project, you will be asked to sign in. Follow
              the prompts to create an account. You will then be able to view and record project
              details (click here to preview). Record the details of the project details in the
              boxes below and comment as to whether it is or is not a suitable project for you and
              why.
            </p>

            <p class="mb-1">Name of Project:</p>
            <text-block v-model="answers.question3" :editable="isEditable" />

            <p class="mb-1">Project Details:</p>
            <text-block v-model="answers.question4" :editable="isEditable" />

            <p class="mb-1">This project is suitable for me:</p>
            <select
              class="defaultselect w-full mb-base mx-1 mt-0"
              v-model="answers.question5"
              aria-label="This project is suitable for me"
            >
              <option value="0">Please Select</option>
              <option value="1">Yes</option>
              <option value="2">No</option>
            </select>

            <p class="mb-1">Why/Why Not?</p>
            <textarea-block v-model="answers.question6" :editable="isEditable" />
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Task 3/Option 3: Participate in a Project">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">Task 3/Option 3: Participate in a Project</h4>
            <p class="mb-5">
              With this option you can take your project a step further and participate! When you
              choose an appropriate project, use the details to make contact with the Contact
              Person. Make sure you have access to the required equipment.
            </p>
            <p class="mb-5">
              When you have completed your project, write about your experience in the box below. It
              can be one sentence or many paragraphs.
            </p>

            <textarea-block v-model="answers.question7" :editable="isEditable" />
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>

    <save-buttons :is-editable="isEditable" @save="save" />
  </div>
</template>

<script>
//import methods from "@/views/WorkExperiences/components/activity_js/methods";
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/envscience/ala_overview_trigger.jpg'),
          video: 'https://www.youtube.com/embed/crg9itOeihE?autoplay=1',
        },
      },
      popupImages: {
        1:
          $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
          '/content/envscience/project_tiles.jpg',
        2:
          $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
          '/content/envscience/project_filter.jpg',
      },
      answers: {
        question1: '',
        question2: '',
        question3: '',
        question4: '',
        question5: 0,
        question6: '',
        question7: '',
      },
      image1: require('@/assets/images/experiences/envscience/project_finder_button.jpg'),
      login_type: localStorage.getItem('login_type'),
    };
  },
  methods: {
    save() {
      this.data = this.answers;
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.data.question1 !== 'undefined') {
      this.answers = this.data;
    }
  },
};
</script>
